import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Loader2, Plus, Trash2, ClipboardList, CheckCircle2, Circle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function AdminTasks() {
  const qc = useQueryClient();
  const [title, setTitle] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-tasks"],
    queryFn: async () => (await api.get("/admin/tasks")).data,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin-tasks"] });

  const createMut = useMutation({
    mutationFn: async (t) => (await api.post("/admin/tasks", { title: t })).data,
    onSuccess: () => { setTitle(""); invalidate(); },
    onError: () => toast.error("Could not add task"),
  });

  const toggleMut = useMutation({
    mutationFn: async (id) => (await api.put(`/admin/tasks/${id}/toggle`)).data,
    onSuccess: invalidate,
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/tasks/${id}`)).data,
    onSuccess: () => { invalidate(); toast.success("Task removed"); },
  });

  const tasks = data?.tasks || [];
  const pending = tasks.filter((t) => !t.done);
  const done = tasks.filter((t) => t.done);

  const submit = (e) => {
    e.preventDefault();
    const t = title.trim();
    if (!t) return;
    createMut.mutate(t);
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-tasks">
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">Admin</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter flex items-center gap-3">
          <ClipboardList className="w-8 h-8 text-primary" /> Task Board
        </h1>
        <p className="text-sm text-muted-foreground mt-2">Track your own work items. Simple checklist — tick when done.</p>
      </div>

      <form onSubmit={submit} className="flex gap-2" data-testid="add-task-form">
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Add a new task…"
          className="rounded-md"
          data-testid="task-input"
        />
        <Button type="submit" className="rounded-md gap-2" disabled={createMut.isPending} data-testid="add-task-button">
          {createMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
        </Button>
      </form>

      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : (
        <div className="space-y-6">
          <section>
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3">
              To do ({pending.length})
            </h3>
            {pending.length ? (
              <div className="space-y-2" data-testid="pending-tasks">
                {pending.map((t) => (
                  <TaskRow key={t.id} task={t} onToggle={toggleMut.mutate} onDelete={deleteMut.mutate} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border rounded-md">
                Nothing pending. Add a task above.
              </p>
            )}
          </section>

          {done.length > 0 && (
            <section>
              <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3">
                Done ({done.length})
              </h3>
              <div className="space-y-2" data-testid="done-tasks">
                {done.map((t) => (
                  <TaskRow key={t.id} task={t} onToggle={toggleMut.mutate} onDelete={deleteMut.mutate} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function TaskRow({ task, onToggle, onDelete }) {
  return (
    <div
      className="flex items-center gap-3 p-3 rounded-md border border-border bg-card group"
      data-testid={`task-row-${task.id}`}
    >
      <button
        onClick={() => onToggle(task.id)}
        className="shrink-0 text-muted-foreground hover:text-primary transition-colors"
        data-testid={`task-toggle-${task.id}`}
        aria-label={task.done ? "Mark as not done" : "Mark as done"}
      >
        {task.done ? <CheckCircle2 className="w-5 h-5 text-success" /> : <Circle className="w-5 h-5" />}
      </button>
      <span className={`flex-1 text-sm ${task.done ? "line-through text-muted-foreground" : ""}`}>
        {task.title}
      </span>
      <button
        onClick={() => onDelete(task.id)}
        className="shrink-0 text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100"
        data-testid={`task-delete-${task.id}`}
        aria-label="Delete task"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}
